import {
  useEffect,
  useLayoutEffect,
  useRef,
  type MutableRefObject,
} from 'react';
import Search from '@arcgis/core/widgets/Search';

type UseMapSearchWidgetArgs = {
  mapView: __esri.MapView | null;
  isActive: boolean;
  searchRef?: MutableRefObject<Search | null>;
  onSelectResult?: (event: __esri.SearchSelectResultEvent) => void;
};

export const useMapSearchWidget = ({
  mapView,
  isActive,
  searchRef,
  onSelectResult,
}: UseMapSearchWidgetArgs) => {
  const onSelectResultRef = useRef(onSelectResult);
  useLayoutEffect(() => {
    onSelectResultRef.current = onSelectResult;
  }, [onSelectResult]);

  useEffect(() => {
    if (!isActive || !mapView) return;

    const search = new Search({ view: mapView });
    mapView.ui.add(search, 'top-right');
    if (searchRef) {
      searchRef.current = search;
    }

    // Stable listener: do not recreate Search when parent callback identity changes.
    const handle = search.on('select-result', (event) => {
      onSelectResultRef.current?.(event);
    });

    return () => {
      handle.remove();
      search.destroy();
      mapView.ui.remove(search);
      if (searchRef) {
        searchRef.current = null;
      }
    };
    // Intentionally omit onSelectResult — held in a ref so typing is not wiped.
  }, [isActive, mapView, searchRef]);
};
